import {
  Building2,
  Landmark,
  Home,
  Car,
  TrendingUp,
  PiggyBank,
  Shield,
  Coins,
  Receipt,
  Package,
  Heart,
  Scale,
  CreditCard,
  Banknote,
  Zap,
  Wallet,
  FileText,
  Briefcase,
  type LucideIcon
} from 'lucide-react';

// =============================================================================
// INVENTORY ITEM TYPE ICONS
// =============================================================================

const TYPE_ICONS: Record<string, LucideIcon> = {
  'bank-account': Landmark,
  superannuation: PiggyBank,
  'srn-shares': TrendingUp,
  'hin-shares': TrendingUp,
  'managed-funds': TrendingUp,
  'private-company': Briefcase,
  'real-estate': Home,
  vehicle: Car,
  'life-insurance': Shield,
  cryptocurrency: Coins,
  'nursing-home-bond': Building2,
  other: Package,
  // Liability type icons
  'funeral-burial': Heart,
  'legal-professional': Scale,
  mortgage: Home,
  'vehicle-loan': Car,
  'tax-liability': Receipt,
  'credit-card': CreditCard,
  'personal-loan': Banknote,
  'utility-bill': Zap,
  'estate-expense': Wallet,
  'other-liability': FileText,
  // Legacy type mappings for backwards compatibility
  bank: Landmark,
  super: PiggyBank,
  shares: TrendingUp,
  property: Home
};

export function getTypeIcon(type: string): LucideIcon {
  return TYPE_ICONS[type] || Building2;
}

// =============================================================================
// TYPE LABELS
// =============================================================================

const TYPE_LABELS: Record<string, string> = {
  'real-estate': 'Real Estate',
  'bank-account': 'Bank Account',
  'nursing-home-bond': 'Nursing Home Bond',
  superannuation: 'Super Fund',
  'srn-shares': 'SRN Shares',
  'hin-shares': 'HIN Shares',
  'life-insurance': 'Life Insurance',
  cryptocurrency: 'Cryptocurrency',
  vehicle: 'Vehicle',
  'managed-funds': 'Managed Funds',
  'private-company': 'Private Company Shares',
  other: 'Other',
  'employment-entitlements': 'Employment Entitlements',
  // Liability type labels
  'funeral-burial': 'Funeral & Burial',
  'legal-professional': 'Legal & Professional',
  mortgage: 'Mortgage',
  'vehicle-loan': 'Vehicle Loan',
  'tax-liability': 'Tax Liability',
  'credit-card': 'Credit Card',
  'personal-loan': 'Personal Loan',
  'utility-bill': 'Utility Bill',
  'estate-expense': 'Estate Expense',
  'other-liability': 'Other Liability'
};

export function getTypeLabel(type: string): string {
  return TYPE_LABELS[type] || type.replace(/-/g, ' ');
}

// =============================================================================
// LINE ITEM DISPLAY FORMATTING
// =============================================================================
//
// Formats how inventory items appear in the balance sheet rows.
// Each item renders two text segments:
//   [label (bold)] [detail (muted)]
//
// Rules by item type:
//   Bank Account    → "Australian Unity Account"       | "082000 - 45678901"
//   SRN Shares      → "STO Shares"                     | "SRN I0078901234"
//   HIN Shares      → "COM Shares"                     | "HIN X1239385949"
//   Real Estate     → "Old Gold Lane..."                | (none)
//   Vehicle         → "Motor Vehicle - Dodge Journey"  | (none)
//   Other           → "Sausage Stand" or "Other"        | (none)
//   Everything else → "Phoenix Health Fund Life Insurance" | "Bronze Max..."
//
// See balance-sheet.stories.tsx NamingRulesCard for a visual reference.
// =============================================================================

/**
 * One piece of a row title, and the item key it came from.
 *
 * **Only a label built from MORE THAN ONE key needs these** (VALE-1375). On 21
 * of the 22 types the title is named after a single key, so striking the whole
 * title strikes exactly the rejected thing. A vehicle is the exception — its
 * title is `Motor Vehicle - {make} {model}` — and rejecting the model struck
 * `Motor Vehicle - Toyota RAV4` entire, claiming the reviewer had turned down
 * the make and the type as well.
 *
 * The split lives here rather than in the review surface for the reason
 * {@link LineItemDisplay.labelKeys} already gives: how a title is composed is
 * this function's own answer, and a second copy of it drifts the moment a
 * naming rule moves.
 */
export interface LineItemLabelSegment {
  text: string;
  /** Absent for fixed connective text, which no reject can turn down. */
  key?: string;
}

export interface LineItemDisplay {
  label: string;
  /**
   * The title split into its keyed parts, for a label built from more than one
   * key (VALE-1375). Absent means the title is one key's, and strikes whole.
   */
  labelSegments?: LineItemLabelSegment[];
  detail: string | null;
  /**
   * **The item keys each half of the line was built from** (VALE-1181), in the
   * dot-notation the AI-extract diff and its per-field rejects speak
   * (`itemInfo.ticker`, `institution`, `description`).
   *
   * They are returned by the formatter rather than re-derived by a caller
   * because the type -> key answer is this function's own: of the 22 registered
   * types the label is the institution on 15, the ticker on the two share
   * types, make and model on a vehicle, and the description on four more. A
   * review surface that has
   * to strike the part of the row line a reviewer turned down needs exactly
   * that answer, and a second copy of it drifts the moment a naming rule moves
   * - which is how a rejected ticker went on asserting itself on the row line
   * while the institution beside it struck correctly.
   *
   * Empty where that half is built from no item key at all (a bare type label).
   */
  labelKeys: string[];
  detailKeys: string[];
}

// Shortened type labels used when institution provides context
const COMPACT_TYPE_LABELS: Record<string, string> = {
  'bank-account': 'Account'
};

function getCompactTypeLabel(type: string): string {
  return COMPACT_TYPE_LABELS[type] || getTypeLabel(type);
}

// Types where description + type name form the label (no detail)
// `vehicle` is handled separately (fixed "Motor Vehicle - {make} {model}" prefix form).
const DESCRIPTION_WITH_TYPE_TYPES = new Set([
  'other',
  'real-estate',
  'estate-expense',
  'other-liability'
]);

// ---------------------------------------------------------------------------
// Shares helpers
// ---------------------------------------------------------------------------

function getShareTicker(
  type: string,
  itemInfo?: Record<string, unknown> | null
): string | undefined {
  if (type === 'hin-shares') {
    // Persisted HIN shares (both manual and AI Matter Creation) carry the
    // ticker under portfolio[0]; `stock` is a UI-only form field that's
    // stripped before save, so it's never present on items the balance sheet
    // renders. Read the canonical persisted shape first (top-level, then
    // portfolio[0]), falling back to `stock` only for in-form display.
    const holding = (
      itemInfo?.portfolio as Array<Record<string, unknown>> | undefined
    )?.[0];
    const stock = itemInfo?.stock as Record<string, unknown> | undefined;
    return (
      (itemInfo?.ticker as string | undefined) ??
      (holding?.ticker as string | undefined) ??
      (stock?.ticker as string | undefined)
    );
  }
  return itemInfo?.ticker as string | undefined;
}

function getShareReference(
  type: string,
  itemInfo?: Record<string, unknown> | null
): string | null {
  if (type === 'hin-shares') {
    const holding = (
      itemInfo?.portfolio as Array<Record<string, unknown>> | undefined
    )?.[0];
    const hin =
      (itemInfo?.hin as string | undefined) ??
      (holding?.hin as string | undefined);
    return hin ? `HIN ${hin}` : null;
  }
  const srn = itemInfo?.srn as string | undefined;
  return srn ? `SRN ${srn}` : null;
}

// ---------------------------------------------------------------------------
// Main formatter
// ---------------------------------------------------------------------------

export function formatLineItemDisplay(
  type: string,
  institutionName?: string | null,
  itemInfo?: Record<string, unknown> | null,
  description?: string | null
): LineItemDisplay {
  // Shares: "{ticker} Shares" with SRN/HIN reference as detail
  if (type === 'srn-shares' || type === 'hin-shares') {
    const ticker = getShareTicker(type, itemInfo);
    const reference = getShareReference(type, itemInfo);

    return {
      label: ticker ? `${ticker} Shares` : 'Shares',
      detail: reference,
      /*
        Both share types key their Ticker row `itemInfo.ticker`, whichever
        nested shape `getShareTicker` ends up reading it out of - the drawer
        spec is what a reject is keyed by, not the storage path.
      */
      labelKeys: ['itemInfo.ticker'],
      detailKeys: [type === 'hin-shares' ? 'itemInfo.hin' : 'itemInfo.srn']
    };
  }

  // Vehicle: fixed "Motor Vehicle" prefix + make/model (LF-4063). The vehicleType
  // field is intentionally not used, and there is no trailing "Vehicle" word. Falls
  // back to "Motor Vehicle" alone when make/model are missing — never a dangling "-".
  if (type === 'vehicle') {
    const make = typeof itemInfo?.make === 'string' ? itemInfo.make.trim() : '';
    const model =
      typeof itemInfo?.model === 'string' ? itemInfo.model.trim() : '';
    const makeModel = [make, model].filter(Boolean).join(' ');

    return {
      label: makeModel ? `Motor Vehicle - ${makeModel}` : 'Motor Vehicle',
      /*
        The one multi-key title (VALE-1375), so the one that has to say how it
        was built: rejecting the model must strike `RAV4` and leave
        `Motor Vehicle - Toyota` standing.
      */
      labelSegments: makeModel
        ? [
            { text: 'Motor Vehicle - ' },
            ...(make ? [{ text: make, key: 'itemInfo.make' }] : []),
            ...(make && model ? [{ text: ' ' }] : []),
            ...(model ? [{ text: model, key: 'itemInfo.model' }] : [])
          ]
        : undefined,
      detail: null,
      labelKeys: ['itemInfo.make', 'itemInfo.model'],
      detailKeys: []
    };
  }

  // Standalone types: "{description} {type name}" with no detail
  // Exception: "other" uses description alone when available, falling back to "Other"
  if (DESCRIPTION_WITH_TYPE_TYPES.has(type)) {
    const typeLabel = getTypeLabel(type);

    if (
      type === 'other' ||
      type === 'estate-expense' ||
      type === 'other-liability' ||
      type === 'real-estate'
    ) {
      return {
        label: description || typeLabel,
        detail: null,
        labelKeys: ['description'],
        detailKeys: []
      };
    }

    const prefix = description ? `${description} ` : '';

    return {
      label: `${prefix}${typeLabel}`,
      detail: null,
      labelKeys: ['description'],
      detailKeys: []
    };
  }

  // Default: "{institution} {compact type}" with description as detail
  const typeLabel = getCompactTypeLabel(type);

  return {
    label: institutionName ? `${institutionName} ${typeLabel}` : typeLabel,
    detail: description ?? null,
    labelKeys: ['institution'],
    detailKeys: ['description']
  };
}

// =============================================================================
// LIABILITY ICON
// =============================================================================

export function getLiabilityIcon(): LucideIcon {
  return Receipt;
}
